import classNames from 'classnames/bind';
import styles from './ClosedInvoice.module.scss';
import TableClassicReport from '../base/TableClassicReport';
import { useEffect, useState } from 'react';
import { IClosedInvoiceRecord, IPaginationReport } from '~/utils/interface/report';
import reportApi from '~/apis/report';
import { ColumnType } from '~/utils/types/common';
import dayjs from 'dayjs';

const cx = classNames.bind(styles);

const ClosedInvoice = () => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<IClosedInvoiceRecord[]>([]);
  const [pagination, setPagination] = useState<IPaginationReport>({
    totalItems: 0,
    totalPage: 0,
    page: 1,
    limit: 10,
    skip: 0,
  });

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await reportApi.getClosedInvoices({ page: pagination.page });
        setData(response.data?.data?.responses ?? []);
        setPagination(response.data?.data?.pagination ?? null);
        setLoading(false);
      } catch (error) {
        console.error('Failed to fetch closed invoices', error);
        setLoading(false);
      }
    };
    fetchData();
  }, [pagination.page]);

  const columns: Array<ColumnType> = [
    {
      id: "salesRep",
      labelHeader: "Sales Rep",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div
          className={cx("cell")}
        >
          <p className={cx("text-content")}>{invoice.saleRepName || "-"}</p>
        </div>
      ),
    },
    {
      id: "status",
      labelHeader: "Status",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.status || "-"}</p>
        </div>
      ),
    },
    {
      id: "number",
      labelHeader: "Number",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.numberOfInvoices || "-"}</p>
        </div>
      ),
    },
    {
      id: "subTotal",
      labelHeader: "Sub Total",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>${invoice.subTotal || "0"}</p>
        </div>
      ),
    },
    {
      id: "total",
      labelHeader: "Total",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>${invoice.total || "0"}</p>
        </div>
      ),
    },
    {
      id: "totalPaid",
      labelHeader: "Total Paid",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>${invoice.totalPaid || "0"}</p>
        </div>
      ),
    },
    {
      id: "balance",
      labelHeader: "Balance",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>${invoice.balance || "0"}</p>
        </div>
      ),
    },
    {
      id: "relatedRecords",
      labelHeader: "Related Records",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{invoice.relatedContact?.name || "-"} (Contact)</p>
          <p className={cx("text-content")}>{invoice.jobId ? `#${invoice.jobId} (Job)` : "-"}</p>
        </div>
      ),
    },
    {
      id: "dateUpdated",
      labelHeader: "Date Updated",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{dayjs(invoice.dateUpdated).format("MM/DD/YYYY hh:mm A") || "-"}</p>
        </div>
      ),
    },
    {
      id: "dateInvoiced",
      labelHeader: "Date Invoiced",
      renderCell: (invoice: IClosedInvoiceRecord) => (
        <div className={cx("cell")}>
          <p className={cx("text-content")}>{dayjs(invoice.dateInvoiced).format("MM/DD/YYYY") || "-"}</p>
        </div>
      ),
    },
  ];

  return (
    <TableClassicReport<IClosedInvoiceRecord>
      title="Closed Invoices"
      columns={columns}
      data={data}
      page={Number(pagination.page)}
      totalPages={pagination.totalPage}
      totalItems={pagination.totalItems}
      itemsPerPage={pagination.limit}
      onPageChange={(page) => {
        setPagination({ ...pagination, page });
      }}
      isLoading={loading}
    />
  );
};

export default ClosedInvoice;
